import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

/* styles */
import "/styles/modern-normalize.css";
import "/styles/global.css";
import "/styles/components/foodimpactdiagram.css";
import "/styles/utility.css";

const FLCSunburstDiagram = () => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [data, setData] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Production");
  const [isAnimationTriggered, setIsAnimationTriggered] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState("Milanote Articles");

  const width = 450;
  const height = 450;
  const innerRadius = 60;
  const outerRadius = Math.min(width, height) / 2.5;

  const arcGenerator = d3
    .arc()
    .innerRadius(innerRadius)
    .padAngle(0.01)
    .padRadius(innerRadius);

  const getDefinition = (category) => {
    const found = data.find((d) => d["Cycle"] === category);
    return found?.Definition || "";
  };

  useEffect(() => {
    d3.csv("./data/Food Life Cycle.csv", d3.autoType).then((csvData) => {
      setData(csvData);
    });
  }, []);

  const renderChart = () => {
    const filteredData = data.map((d) => ({
      category: d["Cycle"],
      value: +d[selectedMetric],
    }));

    const x = d3
      .scaleBand()
      .domain(filteredData.map((d) => d.category))
      .range([0, 2 * Math.PI])
      .align(0);

    const y = d3
      .scaleRadial()
      .domain([0, d3.max(filteredData, (d) => d.value) + 10])
      .range([innerRadius, outerRadius]);

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .style("overflow", "visible");

    const g = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    g.selectAll("path")
      .data(filteredData)
      .join("path")
      .attr("class", (d) =>
        d.category === activeCategory
          ? "sunburst__chart-area sunburst__chart-area--active"
          : "sunburst__chart-area"
      )
      .attr(
        "d",
        arcGenerator
          .outerRadius((d) => y(d.value))
          .startAngle((d) => x(d.category))
          .endAngle((d) => x(d.category) + x.bandwidth())
      )
      .on("click", (event, d) => {
        setActiveCategory(d.category);
        updateActiveCategory(d.category);
      });

    // Add arc labels
    g.append("g")
      .attr("text-anchor", "middle")
      .selectAll("g")
      .data(x.domain())
      .join("g")
      .attr(
        "transform",
        (d) =>
          `rotate(${
            ((x(d) + x.bandwidth() / 2) * 180) / Math.PI - 90
          }) translate(${outerRadius},0)`
      )
      .call((g) =>
        g
          .append("text")
          .attr("transform", (d) =>
            (x(d) + x.bandwidth() / 2 + Math.PI / 2) % (2 * Math.PI) < Math.PI
              ? "rotate(90)translate(0,16)"
              : "rotate(-90)translate(0,-9)"
          )
          .text((d) => {
            const val = filteredData.find((item) => item.category === d)?.value;
            return `${d}: ${val}`;
          })
          .attr("class", "sunburst__arc-label")
      );
  };

  const updateActiveCategory = (newCategory) => {
    d3.select(svgRef.current)
      .selectAll("path")
      .attr("class", (d) =>
        d.category === newCategory
          ? "sunburst__chart-area sunburst__chart-area--active"
          : "sunburst__chart-area"
      );
  };

  useEffect(() => {
    if (!data.length) return;

    const filteredData = data.map((d) => ({
      category: d["Cycle"],
      value: +d[selectedMetric],
    }));

    const x = d3
      .scaleBand()
      .domain(filteredData.map((d) => d.category))
      .range([0, 2 * Math.PI])
      .align(0);

    const y = d3
      .scaleRadial()
      .domain([0, d3.max(filteredData, (d) => d.value) + 10])
      .range([innerRadius, outerRadius]);

    const triggerAnimation = () => {
      d3.select(svgRef.current)
        .selectAll("path")
        .attr("d", arcGenerator.outerRadius(innerRadius)) // initial state
        .transition()
        .duration(1000)
        .attrTween("d", function (d) {
          const interpolate = d3.interpolate(innerRadius, y(d.value));
          return (t) =>
            arcGenerator
              .outerRadius(interpolate(t))
              .startAngle(x(d.category))
              .endAngle(x(d.category) + x.bandwidth())(d);
        });
    };

    renderChart();

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isAnimationTriggered) {
          setIsAnimationTriggered(true);
          triggerAnimation();
        }
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [data, selectedMetric]);

  return (
    <div ref={containerRef} className="treemap__container">
      <h2 className="diagram__header">Food Life Cycle</h2>
      <h3 className="diagram__subheader">
        Information Availability by Section
      </h3>
      <svg ref={svgRef}></svg>
      <p className="diagram__text">
        This Sunburst Diagram visualizes the availability of information across
        different sections of the food life cycle. Current section is{" "}
        <strong>{activeCategory}</strong>: {getDefinition(activeCategory)}.
        Select another section to update definition. <br /> <br />
        Current method of data collection is {selectedMetric}. Click on button
        below to update the method on how the data were collected.
      </p>
      <h3 className="diagram__text-bold">Click for information category</h3>

      <div className="treemap__buttons">
        {[
          "Milanote Articles",
          "Google Scholar 1st method",
          "Google Scholar 2nd method",
        ].map((label) => (
          <button
            key={label}
            className={`treemap__buttons ${
              selectedMetric === label ? "active" : ""
            }`}
            onClick={() => {
              setSelectedMetric(label);
              setIsAnimationTriggered(false); // Reset animation state to replay animation
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default FLCSunburstDiagram;
